import React, { FC, useState } from "react";

import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useMount } from "react-use";

import NotAuthenticatedFormLayout from "@/components/layouts/NotAuthenticated/NotAuthenticatedFormLayout";
import { FlowSteps } from "@/components/molecules";
import { ResetPasswordState } from "@/shared/domain";
import { useNavigation, useRouterHook } from "@/shared/hooks";
import * as tagManager from "@/tagManager";
import { useUserFeedback } from "@/userFeedback";

import ConfirmEmail from "./components/ConfirmEmail/ConfirmEmail";
import EmailSent from "./components/EmailSent/EmailSent";
import NewPassword from "./components/NewPassword/NewPassword";
import PasswordUpdated from "./components/PasswordUpdated/PasswordUpdated";
import { ResetPasswordSteps, stepperData } from "./utils";

const ResetPassword: FC = () => {
    const {
        state: { email: emailState, officeId },
    } = useRouterHook<ResetPasswordState>();
    const { t } = useTranslation();
    const { token } = useParams<any>();
    const { navToRoot } = useNavigation();
    const { incUsageCount } = useUserFeedback();
    const [email, setEmail] = useState(emailState || "");

    const [currentStep, setCurrentStep] = useState(ResetPasswordSteps.CONFIRM_EMAIL);

    useMount(incUsageCount);
    useMount(() => {
        if (token) {
            setCurrentStep(ResetPasswordSteps.NEW_PASSWORD);
        }
        if (!email && currentStep !== ResetPasswordSteps.CONFIRM_EMAIL) {
            navToRoot();
        }
    });

    const handlePasswordResetRequest = (emailSent) => {
        setEmail(emailSent);
        setCurrentStep(ResetPasswordSteps.EMAIL_SENT);
        tagManager.pushChangePasswordBeginEvent();
    };

    const handleSetPassword = () => {
        setCurrentStep(ResetPasswordSteps.PASSWORD_UPDATED);
    };

    const displayStep = () => {
        switch (currentStep) {
            case ResetPasswordSteps.CONFIRM_EMAIL:
                return (
                    <ConfirmEmail
                        email={email}
                        officeId={officeId}
                        onResetRequest={(emailSent) => handlePasswordResetRequest(emailSent)}
                    />
                );
            case ResetPasswordSteps.EMAIL_SENT:
                return <EmailSent email={email} />;
            case ResetPasswordSteps.NEW_PASSWORD:
                return <NewPassword token={token} onResetComplete={handleSetPassword} />;
            case ResetPasswordSteps.PASSWORD_UPDATED:
                return <PasswordUpdated />;
            default:
                return null;
        }
    };

    return (
        <NotAuthenticatedFormLayout
            title={t("PageTitles.ResetPassword")}
            flowStepsComponent={
                <FlowSteps title={t("ResetPassword.Title")} availableSteps={stepperData} currentStep={currentStep} />
            }
        >
            {displayStep()}
        </NotAuthenticatedFormLayout>
    );
};

export default ResetPassword;
